<template>
  <div class="nws-forecast-card">
    <h2>NWS Forecast</h2>

    <div v-if="loading" class="loading">Loading...</div>
    <div v-else-if="error" class="error">Error: {{ error }}</div>
    <div v-else>
      <table class="forecast-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Forecast</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="forecastData in formattedForecasts" :key="forecastData.dateKey">
            <td>{{ forecastData.date }}</td>
            <td>
              <p
                v-for="entry in forecastData.entries"
                :key="entry.id"
                :class="{ 'marine-forecast': entry.source === 'marine' }"
              >
                <strong>{{ entry.name }}: </strong>{{ entry.text }}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { useWeatherStore } from '@/stores/weatherStore';
import { storeToRefs } from 'pinia';

export default {
  name: 'NWSForecastCard',
  props: {
    showMarineData: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const weatherStore = useWeatherStore();
    const { locations } = storeToRefs(weatherStore);

    return {
      locations,
    };
  },
  computed: {
    forecastLocations() {
      if (!this.locations || !Array.isArray(this.locations)) {
        return [];
      }
      return this.locations.filter(
        (loc) =>
          loc.source === 'nwsForecast' ||
          (this.showMarineData && loc.source === 'marine')
      );
    },
    loading() {
      return this.forecastLocations.some((loc) => loc.loading);
    },
    error() {
      const errorLoc = this.forecastLocations.find((loc) => loc.error);
      return errorLoc ? errorLoc.error : null;
    },
    formattedForecasts() {
      const locations = this.forecastLocations;
      if (locations.length === 0) {
        return [];
      }

      // Collect all unique date keys from active forecast locations
      const dateKeysSet = new Set();
      locations.forEach((loc) => {
        if (loc.forecast && typeof loc.forecast === 'object') {
          Object.keys(loc.forecast).forEach((key) => dateKeysSet.add(key));
        }
      });

      // Sort keys chronologically (e.g. YYYYMMDD-Weekday-Day/Night)
      const sortedKeys = Array.from(dateKeysSet).sort();

      return sortedKeys.map((dateKey) => {
        const entries = locations.map((loc) => ({
          id: loc.id,
          name: loc.name,
          source: loc.source,
          text: (loc.forecast && loc.forecast[dateKey]) || 'N/A',
        }));

        return {
          dateKey,
          date: this.formatDateKey(dateKey),
          entries,
        };
      });
    },
  },
  methods: {
    formatDateKey(dateKey) {
      if (typeof dateKey !== 'string') {
        return 'Invalid Date';
      }

      const parts = dateKey.split('-');
      if (parts.length < 3) {
        return dateKey;
      }

      const dayNight = parts.pop();
      const weekDay = parts.pop();
      const monthDay = parts.pop().slice(-2);

      return `${weekDay} ${monthDay} ${dayNight}`;
    },
  },
};
</script>

<style scoped>
.nws-forecast-card {
  border: 0px solid #ccc;
  padding: 10px;
  margin: 5px;
  border-radius: 5px;
  font-size: 0.9em;
}

.forecast-table {
  width: 95%;
  border-collapse: collapse;
}

.forecast-table table td strong { /* Target strong tags within table cells */
  font-weight: bold; 
}

.forecast-table p { 
  margin-top: 2px;
  margin-bottom: 5px;
}

.marine-forecast {
  background-color: lightblue; /* Or any shade of blue you prefer */
}

.forecast-table th,
.forecast-table td {
  text-align: left;
  padding: 8px;
  border: 1px solid #ccc;
}

.loading,
.error {
  text-align: center;
  font-size: 1.2em;
  margin-top: 20px;
}
</style>